// src/notifications.js
import { db } from './firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';

const PREF_KEY_MAP = {
  score_approved: 'roundApproved',
  score_disputed: 'roundDisputed',
  new_event: 'newEvent',
  position_change: 'positionChange',
};

// Admin-controlled kill switch (AdminPanel > Notifications tab), stored at
// settings/notificationConfig. Missing doc or missing key both mean "on" —
// only an explicit `false` suppresses a type, so this can never silently
// disable a type nobody has configured yet.
export const NOTIFICATION_TYPES = [
  { key: 'round_submitted', label: 'Round submitted (to player)' },
  { key: 'round_pending_review', label: 'Round awaiting approval (to marker)' },
  { key: 'score_approved', label: 'Score approved' },
  { key: 'score_disputed', label: 'Score disputed' },
  { key: 'new_event', label: 'New event posted' },
  { key: 'position_change', label: 'Leaderboard position change' },
  { key: 'trophy_earned', label: 'Trophy earned' },
];

export async function getGlobalNotificationSettings() {
  try {
    const snap = await getDoc(doc(db, 'settings', 'notificationConfig'));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.error('Failed to load global notification settings:', e);
    return {};
  }
}

export async function createNotification(userId, type, extraData = {}) {
  const templates = {
    score_approved: {
      title: 'Score Approved ✅',
      message: extraData.date
        ? `Your round from ${new Date(extraData.date).toLocaleDateString()} has been approved!`
        : 'Your round has been approved by your marker.',
      icon: '✅',
    },
    score_disputed: {
      title: 'Score Disputed ⚠️',
      message: extraData.reason
        ? `Your round has been disputed. Reason: ${extraData.reason}`
        : 'Your round has been disputed. Please check with your marker.',
      icon: '⚠️',
    },
    round_pending_review: {
  title: 'Round Awaiting Your Approval ⏳',
  message: `${extraData.playerName || 'A player'} submitted their score for ${extraData.eventName || 'an event'}.`,
  icon: '⏳',
},
    new_event: {
      title: 'New Event Posted',
      message: extraData.eventName
        ? `${extraData.eventName}${extraData.eventDate ? ` - ${new Date(extraData.eventDate).toLocaleDateString()}` : ''} 🏌️`
        : 'A new event has been added.',
      icon: '🏌️',
    },
    position_change: {
      title: 'Leaderboard Update',
      message: extraData.direction === 'up'
        ? `You moved up ${Math.abs(extraData.change)} spots to #${extraData.newPosition}! 📈`
        : `You dropped ${Math.abs(extraData.change)} spots to #${extraData.newPosition} 📉`,
      icon: extraData.direction === 'up' ? '📈' : '📉',
    },
    round_submitted: {
      title: 'Round Submitted',
      message: 'Your round is pending approval by your marker.',
      icon: '⏳',
    },
    trophy_earned: {
      title: 'Trophy Earned! 🏆',
      message: extraData.trophyName ? `You earned the "${extraData.trophyName}" trophy!` : 'You earned a new trophy!',
      icon: '🏆',
    },
  };

  const template = templates[type];
  if (!template) {
    console.warn('Unknown notification type:', type);
    return;
  }

  const globalSettings = await getGlobalNotificationSettings();
  if (globalSettings[type] === false) return;

  const prefKey = PREF_KEY_MAP[type];
  if (prefKey) {
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      const notifPrefs = userSnap.data()?.notificationPrefs;
      if (notifPrefs && notifPrefs[prefKey] === false) return;
    } catch (e) { /* allow through if fetch fails */ }
  }

  try {
    await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      ...template,
      read: false,
      timestamp: serverTimestamp(),
      metadata: extraData,
    });
  } catch (err) {
    console.error('Failed to create notification:', err);
  }
}
